// Angular imports
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// RxJs imports
import { Observable, Subject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// 3rd party imports
import { plainToInstance } from 'class-transformer';

// Service imports
import { EnvironmentConfigService } from './environment-config.service';

// Model imports
import { RealAssistSessionDto } from '../_models/real-assist/real-assist-session-dto';
import { RealAssistFeedbackDto } from '../_models/real-assist/real-assist-feedback-dto';

// Constant imports
import { StorageKeyConstants } from '../_constants/storage-key-constants';

export interface StreamingMessage {
  content: string;
  isComplete: boolean;
  responseId?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RealAssistService implements OnDestroy {
  private _sessionId: string | null = null;
  private _streamingSubject = new Subject<StreamingMessage>();

  constructor(
    private http: HttpClient,
    private envConfig: EnvironmentConfigService,
    private ngZone: NgZone
  ) {
    this.initializeSession();
  }

  ngOnDestroy(): void {
    this._streamingSubject.complete();
  }

  /**
   * Initialize session for non-logged users.
   * Always creates a new session on page load for a fresh conversation.
   * Old sessions expire automatically on the backend.
   */
  initializeSession(): string {
    // Always create a new session for non-logged users on page load
    const newSessionId = this.generateUUID();
    localStorage.setItem(StorageKeyConstants.realAssistSessionId, newSessionId);
    this._sessionId = newSessionId;

    return newSessionId;
  }

  /**
   * Get the current session ID
   */
  get sessionId(): string {
    if (!this._sessionId) {
      return this.initializeSession();
    }
    return this._sessionId;
  }

  /**
   * Clear the current session and generate a new one
   */
  resetSession(): string {
    localStorage.removeItem(StorageKeyConstants.realAssistSessionId);
    this._sessionId = null;
    return this.initializeSession();
  }

  /**
   * Generate a cryptographically secure UUID v4
   */
  private generateUUID(): string {
    return crypto.randomUUID();
  }

  /**
   * Get streaming messages observable
   */
  getStreamingMessages(): Observable<StreamingMessage> {
    return this._streamingSubject.asObservable();
  }

  /**
   * Send a message and stream the response
   */
  sendMessage(message: string): void {
    const url = this.envConfig.realAssistStreamingUrl;

    // Normalize message to lowercase for consistent vector search results
    const normalizedMessage = message.toLowerCase().trim();

    const body = {
      sessionId: this.sessionId,
      userId: this.sessionId, // TODO: Use actual userId when available for logged-in users
      message: normalizedMessage,
      responseMode: 'minimal',
      includeCitations: false
    };

    // Use fetch API for streaming support
    this.ngZone.runOutsideAngular(() => {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return this.handleStreamingResponse(response);
        })
        .catch((error) => {
          this.ngZone.run(() => {
            this._streamingSubject.next({
              content: '',
              isComplete: true,
              error: error.message || 'Failed to connect to Real Assist'
            });
          });
        });
    });
  }

  /**
   * Handle the streaming response from the server (SSE format)
   */
  private async handleStreamingResponse(response: Response): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      this.ngZone.run(() => {
        this._streamingSubject.next({
          content: '',
          isComplete: true,
          error: 'Unable to read response stream'
        });
      });
      return;
    }

    const decoder = new TextDecoder();
    let accumulatedContent = '';
    let responseId: string | undefined;
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Emit final content
          this.ngZone.run(() => {
            this._streamingSubject.next({
              content: accumulatedContent,
              isComplete: true,
              responseId
            });
          });
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete SSE lines
        const lines = buffer.split('\n');
        // Keep last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;

            try {
              const data = JSON.parse(jsonStr);

              // Handle different message types
              switch (data.type) {
                case 'chunk':
                  // Content chunk - accumulate it
                  if (data.content) {
                    accumulatedContent += data.content;
                    this.ngZone.run(() => {
                      this._streamingSubject.next({
                        content: accumulatedContent,
                        isComplete: false,
                        responseId
                      });
                    });
                  }
                  break;

                case 'metadata':
                  // Final metadata - extract responseId if available
                  if (data.metadata?.responseId) {
                    responseId = data.metadata.responseId;
                  }
                  break;

                case 'complete':
                  // Completion message with session info
                  if (data.responseId) {
                    if (typeof data.responseId === 'string') {
                      responseId = data.responseId;
                    } else if (
                      data.responseId.conversationHistory?.length > 0 &&
                      data.responseId.conversationHistory[0].id
                    ) {
                      // Extract the response ID from the latest conversation history entry
                      responseId = data.responseId.conversationHistory[0].id;
                    }
                  }
                  break;

                // Ignore other types: connected, context_description, classification
                default:
                  break;
              }
            } catch {
              // Skip malformed SSE data and continue processing
            }
          }
        }
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Stream reading failed';
      this.ngZone.run(() => {
        this._streamingSubject.next({
          content: accumulatedContent,
          isComplete: true,
          error: errorMessage
        });
      });
    }
  }

  /**
   * Get a specific session by ID
   * GET /sessions/{sessionId}
   */
  getSession(sessionId: string): Observable<RealAssistSessionDto | null> {
    const url = `${this.envConfig.realAssistApiUrl}/sessions/${sessionId}`;
    return this.http.get<RealAssistSessionDto>(url).pipe(
      map((response) => plainToInstance(RealAssistSessionDto, response)),
      catchError(() => of(null))
    );
  }

  /**
   * Get all sessions for a user
   * GET /users/{userId}/sessions
   */
  getUserSessions(userId: string): Observable<RealAssistSessionDto[]> {
    const url = `${this.envConfig.realAssistApiUrl}/users/${userId}/sessions`;
    return this.http.get<RealAssistSessionDto[]>(url).pipe(
      map((response) => plainToInstance(RealAssistSessionDto, response)),
      catchError(() => of([]))
    );
  }

  /**
   * Delete a session
   * DELETE /sessions/{sessionId}
   */
  deleteSession(sessionId: string): Observable<void> {
    const url = `${this.envConfig.realAssistApiUrl}/sessions/${sessionId}`;
    return this.http.delete<void>(url);
  }

  /**
   * Submit feedback for a response
   * PUT /sessions/{sessionId}/responses/{responseId}/feedback
   * Uses fetch API to avoid credentials interceptor (CORS issue with wildcard origin)
   */
  submitFeedback(
    sessionId: string,
    responseId: string,
    rating: 'positive' | 'negative',
    comment?: string
  ): Observable<void> {
    const url = `${this.envConfig.realAssistApiUrl}/sessions/${sessionId}/responses/${responseId}/feedback`;
    const body: RealAssistFeedbackDto = {
      userId: sessionId, // TODO: Use actual userId when available for logged-in users
      rating,
      feedback: comment || (rating === 'positive' ? 'Helpful response' : '')
    };

    return new Observable<void>((observer) => {
      fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          observer.next();
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  /**
   * Submit feedback for the current session
   */
  submitCurrentSessionFeedback(
    responseId: string,
    feedback: 'positive' | 'negative',
    comment?: string
  ): Observable<void> {
    return this.submitFeedback(this.sessionId, responseId, feedback, comment);
  }
}
